import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { io } from 'socket.io-client'
import getSocketUrl from 'helpers/socketUrl'
import {
  buildAccountSessionQuery,
  getAccountBrowserSessionId,
  getAccountClientSessionId,
  getAccountRequestId,
  setAccountRequestId,
} from 'helpers/accountSession'
import { AuthContext } from './AuthContext'

const roomAvailableEventName = 'roomAvailable'
const callFinishedEventName = 'callFinished'

export const AccountDataContext = createContext({
  ws: {},
  availableRoom: {},
  setAvailableRoom: () => {},
})

const AccountDataProvider = ({ children }) => {
  const { user } = useContext(AuthContext)
  const navigate = useNavigate()

  const [ws, setWs] = useState(null)
  const [socketConnected, setSocketConnected] = useState(false)
  const [availableRoom, setAvailableRoom] = useState(null)
  const clientSessionId = useMemo(() => getAccountClientSessionId(), [])
  const browserSessionId = useMemo(() => getAccountBrowserSessionId(user.id), [user.id])
  const [currentRequestId, setCurrentRequestIdState] = useState(
    () => getAccountRequestId(browserSessionId),
  )

  const setCurrentRequestId = useCallback((requestId) => {
    setAccountRequestId(requestId, browserSessionId)
    setCurrentRequestIdState(requestId || null)
  }, [browserSessionId])

  const fetchOngoingCall = useCallback(async () => {
    try {
      const query = buildAccountSessionQuery({ browserSessionId, clientSessionId })
      const res = await fetch(`${process.env.REACT_APP_API_DOMAIN}/accounts/calls?${query}`, {
        credentials: 'include',
        headers: {
          authorization: `Bearer ${user.accessToken}`,
        },
      })
      const json = await res.json()
      if (res.ok && json?.roomId) {
        setAvailableRoom(json)
        setCurrentRequestId(json.requestId)
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log({ accountCallSyncErr: err?.message || err })
    }
  }, [browserSessionId, clientSessionId, setCurrentRequestId, user?.accessToken])

  useEffect(() => {
    const ws = io(getSocketUrl(), {
      auth: {
        token: `Bearer ${user.accessToken}`,
        browserSessionId,
        clientSessionId,
      },
      withCredentials: true,
      transports: ['websocket', 'polling'],
    })
    const handleRoomAvailable = (data) => {
      setAvailableRoom(data)
      if (data?.requestId) setCurrentRequestId(data.requestId)
    }
    const handleCallFinished = (data) => {
      const activeRequestId = getAccountRequestId(browserSessionId)
      if (data?.requestId && activeRequestId && data.requestId !== activeRequestId) return
      setAvailableRoom(null)
      setCurrentRequestId(null)
      window.setTimeout(() => {
        navigate('/accounts/callFinished', { replace: true })
      }, 200)
    }

    ws.on(roomAvailableEventName, handleRoomAvailable)
    ws.on(callFinishedEventName, handleCallFinished)
    ws.on('connect', () => {
      setSocketConnected(true)
      fetchOngoingCall()
    })
    ws.on('disconnect', () => setSocketConnected(false))
    const pingInterval = setInterval(() => {
      ws.emit('ping')
    }, 25_000)
    const handlePageHide = () => {
      const requestId = getAccountRequestId(browserSessionId)
      if (requestId && ws.connected) {
        ws.emit('accountPageClosing', { requestId, clientSessionId })
        fetch(
          `${process.env.REACT_APP_API_DOMAIN}/accounts/pageClosing?${buildAccountSessionQuery({ browserSessionId, clientSessionId })}`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { authorization: `Bearer ${user.accessToken}` },
            keepalive: true,
          },
        ).catch(() => {})
      }
    }
    setWs(ws)
    fetchOngoingCall()
    window.addEventListener('focus', fetchOngoingCall)
    window.addEventListener('pagehide', handlePageHide)
    return () => {
      window.removeEventListener('focus', fetchOngoingCall)
      window.removeEventListener('pagehide', handlePageHide)
      clearInterval(pingInterval)
      ws.off(roomAvailableEventName, handleRoomAvailable)
      ws.off(callFinishedEventName, handleCallFinished)
      ws.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, browserSessionId, clientSessionId, fetchOngoingCall, navigate, setCurrentRequestId])

  const contextValue = useMemo(
    () => ({
      ws,
      socketConnected,
      availableRoom,
      setAvailableRoom,
      browserSessionId,
      clientSessionId,
      currentRequestId,
      setCurrentRequestId,
    }),
    [
      ws,
      socketConnected,
      availableRoom,
      browserSessionId,
      clientSessionId,
      currentRequestId,
      setCurrentRequestId,
    ],
  )
  return (
    <AccountDataContext.Provider value={contextValue}>
      <Outlet />
    </AccountDataContext.Provider>
  )
}

export default AccountDataProvider
